import type { Transport } from '@connectrpc/connect';
import { createTransport } from '@connectrpc/connect/protocol-connect';
import { peerIdFromString } from '@libp2p/peer-id';
import { multiaddr, type Multiaddr } from '@multiformats/multiaddr';
import type { Libp2p } from 'libp2p';

import { sanitizeSidecarDialMultiaddrs } from './multiaddr';

const RPC_PROTOCOL = '/sidecar/rpc/3.0.0';
const RELAY_AUTH_PROTOCOL = '/rematter/auth/1.0.0';
const RELAY_AUTH_SUCCESS = 0x01;
const DEFAULT_DIAL_TIMEOUT_MS = 8_000;

const RPC_DEBUG_PREFIX = '[sidecar-rpc]';

function logRpcError(message: string, details?: unknown): void {
  if (details === undefined) {
    console.error(`${RPC_DEBUG_PREFIX} ${message}`);
    return;
  }
  console.error(`${RPC_DEBUG_PREFIX} ${message}`, details);
}

export type CreateRpcClientOptions = {
  dialTimeoutMs?: number;
  authToken?: string | null;
};

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  operation: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation} timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    promise.then(
      (value) => {
        clearTimeout(timeout);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

function extractRelayBaseAddress(rawAddr: string): string | null {
  const marker = '/p2p-circuit';
  const markerIndex = rawAddr.indexOf(marker);
  if (markerIndex === -1) {
    return null;
  }
  const relayBase = rawAddr.slice(0, markerIndex);
  return relayBase || null;
}

function extractPeerIDFromAddr(rawAddr: string): string | null {
  const match = rawAddr.match(/\/p2p\/([^/]+)$/);
  return match?.[1] ?? null;
}

function toUint8Array(
  chunk: Uint8Array | { subarray: () => Uint8Array },
): Uint8Array {
  return chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk.subarray());
}

function encodeTokenForRelayAuth(token: string): Uint8Array {
  const tokenBytes = new TextEncoder().encode(token);
  const payload = new Uint8Array(4 + tokenBytes.length);
  const view = new DataView(payload.buffer);
  view.setUint32(0, tokenBytes.length, false);
  payload.set(tokenBytes, 4);
  return payload;
}

async function authenticateRelay(
  node: Libp2p,
  relayAddr: Multiaddr,
  authToken: string,
  dialTimeoutMs: number,
): Promise<string> {
  const relayAddrRaw = relayAddr.toString();
  const relayPeerID = extractPeerIDFromAddr(relayAddrRaw);
  if (!relayPeerID) {
    throw new Error(`relay address missing peer id: ${relayAddrRaw}`);
  }

  const relayPeer = peerIdFromString(relayPeerID);
  await node.peerStore.merge(relayPeer, { multiaddrs: [relayAddr] });

  const conn = await withTimeout(
    node.dial(relayPeer),
    dialTimeoutMs,
    `dial relay ${relayPeerID}`,
  );
  const stream = await withTimeout(
    conn.newStream(RELAY_AUTH_PROTOCOL, {
      runOnLimitedConnection: true,
    }),
    dialTimeoutMs,
    `open relay auth stream ${relayPeerID}`,
  );

  try {
    stream.send(encodeTokenForRelayAuth(authToken));

    let statusByte: number | null = null;
    for await (const chunk of stream) {
      const bytes = toUint8Array(chunk);
      if (bytes.length > 0) {
        statusByte = bytes[0];
        break;
      }
    }

    if (statusByte !== RELAY_AUTH_SUCCESS) {
      logRpcError('relay auth rejected', { relayPeerID, statusByte });
      throw new Error(`relay auth rejected by ${relayPeerID}`);
    }

    return relayPeerID;
  } finally {
    try {
      stream.abort(new Error('relay auth cleanup'));
    } catch {
      // stream may already be closed
    }
  }
}

async function authenticateRelaysAndFilter(
  node: Libp2p,
  dialableAddrs: Multiaddr[],
  authToken: string | null | undefined,
  dialTimeoutMs: number,
): Promise<Multiaddr[]> {
  const relayBaseAddrStrings = Array.from(
    new Set(
      dialableAddrs
        .map((addr) => extractRelayBaseAddress(addr.toString()))
        .filter((addr): addr is string => Boolean(addr)),
    ),
  );

  if (relayBaseAddrStrings.length === 0) {
    return dialableAddrs;
  }

  if (!authToken) {
    throw new Error('relay connection requires an authenticated session token');
  }

  const successfulRelayIDs = new Set<string>();
  const relayErrors: unknown[] = [];

  for (const rawRelayAddr of relayBaseAddrStrings) {
    let relayAddr: Multiaddr;
    try {
      relayAddr = multiaddr(rawRelayAddr);
    } catch (error) {
      relayErrors.push(error);
      continue;
    }

    try {
      const relayPeerID = await authenticateRelay(
        node,
        relayAddr,
        authToken,
        dialTimeoutMs,
      );
      successfulRelayIDs.add(relayPeerID);
    } catch (error) {
      relayErrors.push(error);
    }
  }

  if (successfulRelayIDs.size === 0) {
    const directOnlyAddrs = dialableAddrs.filter(
      (addr) => !addr.toString().includes('/p2p-circuit/'),
    );
    if (directOnlyAddrs.length > 0) {
      return directOnlyAddrs;
    }
    throw new AggregateError(relayErrors, 'failed to authenticate relay path');
  }

  return dialableAddrs.filter((addr) => {
    const rawAddr = addr.toString();
    if (!rawAddr.includes('/p2p-circuit/')) {
      return true;
    }
    const relayBaseAddr = extractRelayBaseAddress(rawAddr);
    if (!relayBaseAddr) {
      return false;
    }
    const relayPeerID = extractPeerIDFromAddr(relayBaseAddr);
    return relayPeerID ? successfulRelayIDs.has(relayPeerID) : false;
  });
}

function findCRLF(buf: Uint8Array): number {
  for (let i = 0; i < buf.length - 1; i++) {
    if (buf[i] === 0x0d && buf[i + 1] === 0x0a) return i;
  }
  return -1;
}

type Libp2pHttpRequest = {
  url: string;
  method: string;
  header: Headers;
  body?: AsyncIterable<Uint8Array>;
  signal?: AbortSignal;
};

type Libp2pHttpResponse = {
  status: number;
  header: Headers;
  body: AsyncIterable<Uint8Array>;
  trailer: Headers;
};

function toAbortError(signal: AbortSignal | undefined): Error {
  const reason = signal?.reason;
  if (reason instanceof Error) {
    return reason;
  }
  if (typeof reason === 'string' && reason.trim().length > 0) {
    return new Error(reason);
  }
  return new Error('request aborted');
}

export function createLibp2pHttpClient(
  node: Libp2p,
  remotePeerId: ReturnType<typeof peerIdFromString>,
  dialTimeoutMs: number,
) {
  return async (request: Libp2pHttpRequest): Promise<Libp2pHttpResponse> => {
    const url = new URL(request.url);
    const path = url.pathname;

    const existingConns = node.getConnections(remotePeerId);
    const conn =
      existingConns.find((c) => c.status === 'open') ??
      (await withTimeout(
        node.dial(remotePeerId),
        dialTimeoutMs,
        'dial sidecar for RPC',
      ));

    const stream = await conn.newStream(RPC_PROTOCOL, {
      runOnLimitedConnection: true,
    });
    let abortErr: Error | null = null;
    const abortRequest = () => {
      if (!abortErr) {
        abortErr = toAbortError(request.signal);
      }
      try {
        stream.abort(abortErr);
      } catch {
        /* ignored */
      }
    };

    const ensureNotAborted = () => {
      if (abortErr) {
        throw abortErr;
      }
      if (request.signal?.aborted) {
        abortRequest();
        throw abortErr ?? new Error('request aborted');
      }
    };

    if (request.signal) {
      if (request.signal.aborted) {
        abortRequest();
      }
      request.signal.addEventListener('abort', abortRequest, { once: true });
    }

    try {
      ensureNotAborted();

      const bodyChunks: Uint8Array[] = [];
      let bodyLength = 0;
      if (request.body) {
        for await (const chunk of request.body) {
          ensureNotAborted();
          // Connect may emit an empty chunk for empty protobuf messages.
          // Avoid writing zero-length libp2p stream messages.
          if (chunk.length === 0) {
            continue;
          }
          bodyChunks.push(chunk);
          bodyLength += chunk.length;
        }
      }

      ensureNotAborted();

      const headerLines: string[] = [
        `${request.method} ${path} HTTP/1.1`,
        'Host: sidecar',
      ];
      request.header.forEach((value, key) => {
        const lowerKey = key.toLowerCase();
        if (
          lowerKey !== 'host' &&
          lowerKey !== 'content-length' &&
          lowerKey !== 'transfer-encoding'
        ) {
          headerLines.push(`${key}: ${value}`);
        }
      });
      headerLines.push(`Content-Length: ${bodyLength}`);
      headerLines.push('', '');

      stream.send(new TextEncoder().encode(headerLines.join('\r\n')));
      for (const chunk of bodyChunks) {
        ensureNotAborted();
        stream.send(chunk);
      }

      const streamIter = (
        stream as AsyncIterable<Uint8Array | { subarray: () => Uint8Array }>
      )[Symbol.asyncIterator]();
      let buf = new Uint8Array(0);

      function appendToBuf(data: Uint8Array) {
        const merged = new Uint8Array(buf.length + data.length);
        merged.set(buf);
        merged.set(data, buf.length);
        buf = merged;
      }

      async function readLine(): Promise<string | null> {
        while (true) {
          const idx = findCRLF(buf);
          if (idx !== -1) {
            const line = new TextDecoder().decode(buf.subarray(0, idx));
            buf = buf.subarray(idx + 2);
            return line;
          }
          const { value, done } = await streamIter.next();
          if (done) {
            if (buf.length > 0) {
              logRpcError('rpc response terminated mid-line', {
                method: request.method,
                path,
                bufferedBytes: buf.length,
                preview: new TextDecoder().decode(buf.subarray(0, 120)),
              });
            }
            return null;
          }
          appendToBuf(toUint8Array(value));
        }
      }

      const statusLine = await readLine();
      if (!statusLine) {
        logRpcError('rpc response missing status line', {
          method: request.method,
          path,
          signalAborted: request.signal?.aborted ?? false,
          abortErr: String(abortErr ?? ''),
        });
        if (request.signal?.aborted || abortErr) {
          throw abortErr ?? toAbortError(request.signal);
        }
        throw new Error('empty response from sidecar');
      }
      const statusMatch = statusLine.match(/^HTTP\/1\.\d (\d{3})/);
      if (!statusMatch) throw new Error(`invalid HTTP response: ${statusLine}`);
      const status = parseInt(statusMatch[1], 10);

      const responseHeaders = new Headers();
      while (true) {
        const line = await readLine();
        if (line === null || line === '') break;
        const colonIdx = line.indexOf(':');
        if (colonIdx !== -1) {
          responseHeaders.append(
            line.slice(0, colonIdx),
            line.slice(colonIdx + 1).trimStart(),
          );
        }
      }

      const body: AsyncIterable<Uint8Array> = {
        [Symbol.asyncIterator]() {
          let done = false;
          return {
            async next(): Promise<IteratorResult<Uint8Array>> {
              if (done) return { done: true, value: undefined };

              if (buf.length > 0) {
                const chunk = buf;
                buf = new Uint8Array(0);
                return { done: false, value: chunk };
              }

              const result = await streamIter.next();
              if (result.done) {
                done = true;
                try {
                  await stream.close();
                } catch {
                  /* noop */
                }
                return { done: true, value: undefined };
              }
              return { done: false, value: toUint8Array(result.value) };
            },
            async return(): Promise<IteratorResult<Uint8Array>> {
              done = true;
              try {
                await stream.close();
              } catch {
                /* noop */
              }
              return { done: true, value: undefined };
            },
            async throw(err?: unknown): Promise<IteratorResult<Uint8Array>> {
              done = true;
              try {
                stream.abort(
                  err instanceof Error ? err : new Error('body consumer error'),
                );
              } catch {
                /* ignored */
              }
              return { done: true, value: undefined };
            },
          };
        },
      };

      return {
        status,
        header: responseHeaders,
        body,
        trailer: new Headers(),
      };
    } catch (err) {
      logRpcError('rpc request failed', {
        method: request.method,
        path,
        signalAborted: request.signal?.aborted ?? false,
        error: err instanceof Error ? err.message : String(err),
      });
      try {
        stream.abort(err instanceof Error ? err : new Error(String(err)));
      } catch {
        /* noop */
      }
      throw err;
    } finally {
      request.signal?.removeEventListener('abort', abortRequest);
    }
  };
}

function createLibp2pTransport(
  node: Libp2p,
  remotePeerId: ReturnType<typeof peerIdFromString>,
  dialTimeoutMs: number,
): Transport {
  const httpClient = createLibp2pHttpClient(node, remotePeerId, dialTimeoutMs);

  return createTransport({
    httpClient,
    baseUrl: 'http://sidecar',
    useBinaryFormat: true,
    interceptors: [],
    acceptCompression: [],
    sendCompression: null,
    compressMinBytes: Number.MAX_SAFE_INTEGER,
    readMaxBytes: 0xffffffff,
    writeMaxBytes: 0xffffffff,
  });
}

export async function createRpcTransport(
  node: Libp2p,
  peerId: string,
  multiaddrs: string[],
  options?: CreateRpcClientOptions,
): Promise<{ transport: Transport; connectionType: 'direct' | 'relayed' }> {
  const dialTimeoutMs = options?.dialTimeoutMs ?? DEFAULT_DIAL_TIMEOUT_MS;

  const remotePeerId = peerIdFromString(peerId);
  const dialableAddrs = await authenticateRelaysAndFilter(
    node,
    sanitizeSidecarDialMultiaddrs(multiaddrs),
    options?.authToken,
    dialTimeoutMs,
  );

  if (dialableAddrs.length === 0) {
    throw new Error(
      `no dialable sidecar multiaddrs after filtering (received=${multiaddrs.length})`,
    );
  }

  await node.peerStore.merge(remotePeerId, { multiaddrs: dialableAddrs });

  const initialConnection = await withTimeout(
    node.dial(remotePeerId),
    dialTimeoutMs,
    `dial sidecar ${peerId}`,
  );
  const isRelayed = initialConnection.remoteAddr
    .toString()
    .includes('/p2p-circuit/');
  const connectionType = isRelayed ? 'relayed' : 'direct';

  const transport = createLibp2pTransport(node, remotePeerId, dialTimeoutMs);

  return { transport, connectionType };
}
